import type { NavSectionProps } from 'src/components/nav-section';

import { paths } from 'src/routes/paths';

import { CONFIG } from 'src/global-config';

import { Iconify } from 'src/components/iconify';
import { SvgColor } from 'src/components/svg-color';

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor src={`${CONFIG.assetsDir}/assets/icons/navbar/${name}.svg`} />
);

const ICONS = {
  job: icon('ic-job'),
  blog: icon('ic-blog'),
  chat: icon('ic-chat'),
  mail: icon('ic-mail'),
  user: icon('ic-user'),
  file: icon('ic-file'),
  lock: icon('ic-lock'),
  tour: icon('ic-tour'),
  order: icon('ic-order'),
  label: icon('ic-label'),
  blank: icon('ic-blank'),
  kanban: icon('ic-kanban'),
  folder: icon('ic-folder'),
  course: icon('ic-course'),
  params: icon('ic-params'),
  banking: icon('ic-banking'),
  booking: icon('ic-booking'),
  invoice: icon('ic-invoice'),
  product: icon('ic-product'),
  calendar: icon('ic-calendar'),
  disabled: icon('ic-disabled'),
  external: icon('ic-external'),
  subpaths: icon('ic-subpaths'),
  menuItem: icon('ic-menu-item'),
  ecommerce: icon('ic-ecommerce'),
  analytics: icon('ic-analytics'),
  dashboard: icon('ic-dashboard'),
};

// ----------------------------------------------------------------------

/**
 * Input nav data is an array of navigation section items used to define the structure and content of a navigation bar.
 * Each section contains a subheader and an array of items, which can include nested children items.
 *
 * Each item can have the following properties:
 * - `title`: The title of the navigation item.
 * - `path`: The URL path the item links to.
 * - `icon`: An optional icon component to display alongside the title.
 * - `info`: Optional additional information to display, such as a label.
 * - `allowedRoles`: An optional array of roles that are allowed to see the item.
 * - `caption`: An optional caption to display below the title.
 * - `children`: An optional array of nested navigation items.
 * - `disabled`: An optional boolean to disable the item.
 * - `deepMatch`: An optional boolean to indicate if the item should match subpaths.
 */
export const navData = (t: (key: string) => string): NavSectionProps['data'] => [
  /**
   * Overview
   */
  {
    subheader: t('admin.overview'),
    items: [
      { title: t('admin.home'), path: paths.dashboard.root, icon: ICONS.banking, primary: true },
      {
        title: t('admin.distributors'),
        path: paths.dashboard.artham.distributors,
        icon: <Iconify icon="solar:wallet-money-bold-duotone" width={24} />,
      },
      {
        title: t('admin.dealers'),
        path: paths.dashboard.artham.dealers,
        icon: <Iconify icon="solar:users-group-rounded-bold-duotone" width={24} />,
        primary: true,
      },
      {
        title: t('admin.users'),
        path: paths.dashboard.artham.usersDirectory,
        icon: <Iconify icon="solar:users-group-two-rounded-bold-duotone" width={24} />,
        primary: true,
      },
      {
        title: t('admin.kycQueue'),
        path: paths.dashboard.artham.reviewer.kyc,
        icon: <Iconify icon="solar:user-id-bold-duotone" width={24} />,
      },
      {
        title: t('admin.grievances'),
        path: paths.dashboard.artham.grievances.root,
        icon: <Iconify icon="solar:shield-warning-bold-duotone" width={24} />,
      },
    ],
  },
  /**
   * Money flow
   */
  {
    subheader: t('admin.moneyFlow'),
    items: [
      { title: t('admin.sendMoney'), path: paths.dashboard.artham.send, icon: ICONS.external },
      {
        title: t('admin.collections'),
        path: paths.dashboard.artham.collection,
        icon: <Iconify icon="solar:hand-money-bold-duotone" width={24} />,
      },
      { title: t('admin.history'), path: paths.dashboard.artham.history, icon: ICONS.file, primary: true },
      {
        title: t('admin.ledger'),
        path: paths.dashboard.artham.admin.ledger,
        icon: <Iconify icon="solar:book-2-bold-duotone" width={24} />,
      },
      {
        title: t('admin.disputes'),
        path: paths.dashboard.artham.admin.disputes,
        icon: <Iconify icon="solar:flag-bold-duotone" width={24} />,
      },
      {
        title: t('admin.chargebacks'),
        path: paths.dashboard.artham.chargebacks,
        icon: <Iconify icon="solar:money-bag-bold-duotone" width={24} />,
      },
      {
        title: t('admin.approvalsQueue'),
        path: paths.dashboard.artham.admin.txnApprovals,
        icon: <Iconify icon="solar:check-square-bold-duotone" width={24} />,
        primary: true,
      },
      {
        title: t('admin.stuckSync'),
        path: paths.dashboard.artham.admin.txnStuck,
        icon: <Iconify icon="solar:hourglass-bold-duotone" width={24} />,
      },
      {
        title: t('admin.failedRetries'),
        path: paths.dashboard.artham.admin.txnFailed,
        icon: <Iconify icon="solar:close-circle-bold-duotone" width={24} />,
      },
    ],
  },
  /**
   * Money watch (B5)
   */
  {
    subheader: t('admin.moneyWatch'),
    items: [
      {
        title: t('admin.dailySettlement'),
        path: paths.dashboard.artham.settlement.daily,
        icon: <Iconify icon="solar:calendar-bold-duotone" width={24} />,
      },
      {
        title: t('admin.weeklySettlement'),
        path: paths.dashboard.artham.settlement.weekly,
        icon: <Iconify icon="solar:calendar-mark-bold-duotone" width={24} />,
      },
      {
        title: t('admin.reconciliation'),
        path: paths.dashboard.artham.reconciliation,
        icon: <Iconify icon="solar:checklist-minimalistic-bold-duotone" width={24} />,
      },
      {
        title: t('admin.variance'),
        path: paths.dashboard.artham.variance,
        icon: <Iconify icon="solar:chart-square-bold-duotone" width={24} />,
      },
      {
        title: t('admin.floatNostro'),
        path: paths.dashboard.artham.float,
        icon: <Iconify icon="solar:wallet-2-bold-duotone" width={24} />,
      },
    ],
  },
  /**
   * Reports (B5)
   */
  {
    subheader: t('admin.reports'),
    items: [
      {
        title: t('admin.gmv'),
        path: paths.dashboard.artham.reports.gmv,
        icon: <Iconify icon="solar:chart-2-bold-duotone" width={24} />,
      },
      {
        title: t('admin.commissionPaid'),
        path: paths.dashboard.artham.reports.commissionPaid,
        icon: <Iconify icon="solar:bill-check-bold-duotone" width={24} />,
      },
      {
        title: t('admin.disputeVolume'),
        path: paths.dashboard.artham.reports.disputeVolume,
        icon: <Iconify icon="solar:chat-square-call-bold-duotone" width={24} />,
      },
      {
        title: t('admin.settlementHistory'),
        path: paths.dashboard.artham.reports.settlement,
        icon: <Iconify icon="solar:archive-bold-duotone" width={24} />,
      },
    ],
  },
  /**
   * Commissions (B5)
   */
  {
    subheader: t('admin.commissions'),
    items: [
      {
        title: t('admin.tierSlabs'),
        path: paths.dashboard.artham.commissions.tiers,
        icon: <Iconify icon="solar:layers-bold-duotone" width={24} />,
      },
      {
        title: t('admin.payoutQueue'),
        path: paths.dashboard.artham.commissions.payouts.queue,
        icon: <Iconify icon="solar:hand-money-bold-duotone" width={24} />,
      },
      {
        title: t('admin.tdsReport'),
        path: paths.dashboard.artham.commissions.tds,
        icon: <Iconify icon="solar:document-add-bold-duotone" width={24} />,
      },
    ],
  },
  /**
   * Platform ops
   */
  {
    subheader: t('admin.platformOps'),
    items: [
      {
        title: t('admin.commissionPromos'),
        path: paths.dashboard.artham.admin.promos,
        icon: <Iconify icon="solar:gift-bold-duotone" width={24} />,
      },
      {
        title: t('admin.geofence'),
        path: paths.dashboard.artham.admin.geofence,
        icon: <Iconify icon="solar:map-point-bold-duotone" width={24} />,
      },
      {
        title: t('admin.providerHealth'),
        path: paths.dashboard.artham.admin.providerHealth,
        icon: <Iconify icon="solar:pulse-bold-duotone" width={24} />,
      },
      {
        title: t('admin.otaReleases'),
        path: paths.dashboard.artham.ota,
        icon: <Iconify icon="solar:rocket-bold-duotone" width={24} />,
      },
      {
        title: t('admin.bulkExports'),
        path: paths.dashboard.artham.exports,
        icon: <Iconify icon="solar:download-bold-duotone" width={24} />,
      },
      {
        title: t('admin.notificationTemplates'),
        path: paths.dashboard.artham.admin.notificationsTemplates,
        icon: <Iconify icon="solar:chat-square-like-bold-duotone" width={24} />,
      },
      {
        title: t('admin.auditLog'),
        path: paths.dashboard.artham.admin.auditLog,
        icon: <Iconify icon="solar:document-text-bold-duotone" width={24} />,
      },
      {
        title: t('admin.errorLog'),
        path: paths.dashboard.artham.admin.errorLog,
        icon: <Iconify icon="solar:bug-bold-duotone" width={24} />,
      },
      {
        title: t('admin.ekoLog'),
        path: paths.dashboard.artham.admin.ekoLog,
        icon: <Iconify icon="solar:card-transfer-bold-duotone" width={24} />,
      },
    ],
  },
];
